import { useCallback, useEffect, useRef, useState } from 'react'
import { Shape, ShapeStream } from '@electric-sql/client'

import { authService } from 'services/auth'
import { PanelStore } from 'stores'
import { datadog } from 'utils'
import getEnvFromLocation from 'utils/getEnvFromLocation'

import { ELECTRIC_URLS, REFRESH_KEYCLOAK_TOKEN_LOG, USE_ELECTRIC_LOG } from './const'
import type { ElectricBaseReturn, TElectricParams } from './types'

export const useElectric = <T extends TElectricParams>({
  table,
  variables,
  skip,
}: T['variables']): { data: T['response'][] } & ElectricBaseReturn => {
  const [data, setData] = useState<T['response'][]>([])
  const [shapeError, setShapeError] = useState<Error>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [refetchCounter, setRefetchCounter] = useState(0)

  // Tracks the latest aborter so unsubscribe() / handleRefetch() can act on it.
  const aborterRef = useRef<AbortController>(null)

  useEffect(() => {
    if (skip) {
      return
    }

    // Each effect run owns its own `aborted` flag. All async work checks this flag
    // before writing to React state. When this effect's cleanup runs (chip change,
    // unmount, manual unsubscribe), the flag flips synchronously and every pending
    // callback from this run becomes a no-op. A subsequent effect run gets its own
    // fresh flag, so rapid chip switches can't bleed stale data between contexts.
    const aborter = new AbortController()
    aborterRef.current = aborter

    const init = async () => {
      const currentWorkspaceId = (PanelStore?.selectedPanel as any)?.panelId
      const env = getEnvFromLocation()
      const electricUrl = ELECTRIC_URLS[env]
      const stream = new ShapeStream<T['response']>({
        url: electricUrl,
        headers: {
          // Resolved on every request the stream makes — including each `live=true` long-poll —
          // so a long-open stream always carries a fresh token instead of one frozen at connect
          // time. `authService.getAccessToken()` refreshes when within 30s of expiry, preventing
          // the 401 that used to kill the stream once the 5-min token TTL elapsed.
          Authorization: async () => {
            try {
              return (await authService.getAccessToken()) ?? ''
            } catch (error) {
              datadog.dispatchGenericErrorLog({
                log: REFRESH_KEYCLOAK_TOKEN_LOG,
                error,
              })
              return ''
            }
          },
          'x-insights-workspace': currentWorkspaceId,
        },
        signal: aborter.signal,
        params: { table: table, where: variables },
        onError: error => {
          setShapeError(error)
          datadog.dispatchGenericErrorLog({
            log: USE_ELECTRIC_LOG,
            error,
          })
        },
      })
      const shape = new Shape(stream)

      // The callback runs every time the Shape data changes.
      shape.subscribe(rows => {
        setIsInitialized(true)
        setData(rows?.rows)
      })
    }

    init()

    return () => {
      aborter.abort()
      // Reset data + status synchronously so derived flags (e.g. isEmpty in consumers)
      // don't read stale values from the prior subscription while the new one warms up.
      setData([])
      setShapeError(null)
      setIsInitialized(false)
    }
  }, [skip, table, variables, refetchCounter])

  const unsubscribe = useCallback(() => {
    if (aborterRef.current) {
      aborterRef.current.abort()
    }
  }, [])

  const handleRefetch = useCallback(async () => {
    setRefetchCounter(c => c + 1)
  }, [])

  return { data, unsubscribe, error: shapeError, isInitialized, handleRefetch }
}
